import type React from 'react';
import { reatomContext } from '@reatom/npm-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { GridThemeProvider } from 'styled-bootstrap-grid';

import { DatePickerStyles, CalendarStyles } from '@/js/libs/ui';
import { NotificationsContainer, injectNotificationsStyle } from '@/js/libs/ui/src/V2';
import { AuthProvider } from '@/js/libs/use-auth';
import { UseRequestProvider } from '@/js/libs/use-request';
import { TooltipProvider } from '@/js/libs/use-tooltip';

import 'src/api';
import AnalyticsCollector from 'src/components/AnalyticsCollector';
import ErrorBoundary from 'src/components/ErrorBoundary';
import ErrorScreen from 'src/components/ErrorScreen';
import { gridTheme } from 'src/components/GridTheme';
import { AuthContextProvider } from 'src/components/Layout/AuthIntegration';
import i18n from 'src/i18n';
import { CompanySwitchProvider } from 'src/modules/CompaniesSwitch/context';
import { ConfirmationModal } from 'src/modules/ConfirmModal';
import { ConfirmModalProvider } from 'src/modules/ConfirmModal/Context';
import { useAuthVerificationContext } from 'src/scenes/Authentication/context';
import { ctx, RouterDevtoolsSync } from 'src/store';
import { GlobalStyles } from 'src/theme/GlobalStyles';
import { CodeMirrorStyles } from 'src/theme/libs/CodeMirrorStyles';
import { DayjsInitializer } from './components/DayjsInitializer';
import { ErrorMessagesConfigurator } from './components/ErrorMessagesConfigurator';
import ExternalTokenListener from './components/ExternalTokenListener';
import { NumbersFormatContextProvider } from './components/FormattedNumber/context';
import OneTimeLogout from './components/OneTimeLogout';
import { DEFAULT_DEBOUNCE_DELAY } from './constants';
import { useConfirmationModalContext, useConfirmation } from './hooks/useConfirmation';
import { useRequestErrorNotification } from './hooks/useRequestErrorNotification';

injectNotificationsStyle();

/*
  We need to have a separate wrapper because useRequestErrorNotification should be INSIDE I18nextProvider to have access to 't' function.
 */
const OnFailProvider = ({ children }: React.PropsWithChildren) => {
  const onFail = useRequestErrorNotification();
  return <UseRequestProvider value={{ onFail, debounce: DEFAULT_DEBOUNCE_DELAY }}>{children}</UseRequestProvider>;
};

const Application = () => (
  <>
    <GlobalStyles />
    <I18nextProvider i18n={i18n}>
      <NotificationsContainer />
      <DayjsInitializer />
      <ErrorBoundary errorScreenComponent={ErrorScreen}>
        <AuthProvider>
          <reatomContext.Provider value={ctx}>
            <RouterDevtoolsSync />
            <AnalyticsCollector />
            <CompanySwitchProvider>
              <NumbersFormatContextProvider>
                <HelmetProvider>
                  <AuthContextProvider>
                    <ExternalTokenListener />
                    <OneTimeLogout />
                    <ErrorMessagesConfigurator />
                    <DndProvider backend={HTML5Backend}>
                      <GridThemeProvider gridTheme={gridTheme}>
                        <>
                          <CodeMirrorStyles />
                          <DatePickerStyles />
                          <CalendarStyles />
                          <ConfirmModalProvider>
                            <TooltipProvider>
                              <useAuthVerificationContext.Provider>
                                <ConfirmationModal />
                                <OnFailProvider>
                                  <useConfirmationModalContext.Provider>
                                    <useConfirmation.Modal />
                                    <Outlet />
                                  </useConfirmationModalContext.Provider>
                                </OnFailProvider>
                              </useAuthVerificationContext.Provider>
                            </TooltipProvider>
                          </ConfirmModalProvider>
                        </>
                      </GridThemeProvider>
                    </DndProvider>
                  </AuthContextProvider>
                </HelmetProvider>
              </NumbersFormatContextProvider>
            </CompanySwitchProvider>
          </reatomContext.Provider>
        </AuthProvider>
      </ErrorBoundary>
    </I18nextProvider>
  </>
);

export default Application;
